import { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2, ChevronLeft, Coins, Search, Download, ArrowDownCircle, ArrowUpCircle,
  Mail, User as UserIcon, Trash2,
} from "lucide-react";
import { exportCsv } from "@/utils/csv";

const fmt = (d) => { try { return format(new Date(d), "MMM d, yyyy HH:mm"); } catch { return d || "—"; } };

export default function AdminCreditsLedgerPage() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | credit | debit | payment
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [adjustDirection, setAdjustDirection] = useState("add");
  const [adjustBusy, setAdjustBusy] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/credits/ledger`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load credits ledger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = data?.rows || [];

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (filter === "credit" && (r.delta || 0) <= 0) return false;
      if (filter === "debit" && (r.delta || 0) >= 0) return false;
      if (filter === "payment" && r.kind !== "payment") return false;
      if (search.trim()) {
        const s = search.toLowerCase();
        return (r.description || "").toLowerCase().includes(s)
          || (r.reason || "").toLowerCase().includes(s);
      }
      return true;
    });
  }, [rows, filter, search]);

  const handleExport = () => {
    const headers = ["Date", "Type", "Description", "Reason", "Delta", "Balance after", "Amount USD", "Status"];
    const body = filtered.map(r => [
      r.date || "",
      r.kind || "",
      r.description || "",
      r.reason || "",
      r.delta ?? "",
      r.balance_after ?? "",
      r.amount_usd ?? "",
      r.status || "",
    ]);
    exportCsv(`wokelo-ledger-${(data?.user?.email || userId).replace(/[^a-z0-9]/gi, "_")}.csv`, headers, body);
  };

  const voidEntry = async (row) => {
    if (!row?.id) return;
    const verb = row.delta < 0 ? "credit back" : "deduct";
    const ok = window.confirm(
      `Void this ledger entry?\n\n` +
      `${row.description}\n` +
      `Δ ${row.delta > 0 ? '+' : ''}${row.delta} credits\n\n` +
      `This will ${verb} ${Math.abs(row.delta)} credit${Math.abs(row.delta) !== 1 ? 's' : ''} ${row.delta < 0 ? 'to' : 'from'} the user's balance and notify them.\n\n` +
      `The original row stays in the ledger (strikethrough) for audit.`
    );
    if (!ok) return;
    try {
      const res = await axios.delete(
        `${API}/admin/users/${userId}/credits/ledger/${row.id}`,
        { withCredentials: true }
      );
      toast.success(`Entry voided — new balance: ${res.data.new_balance}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to void entry");
    }
  };

  const submitAdjust = async () => {
    const num = parseInt(adjustAmount, 10);
    if (!num || num <= 0) { toast.error("Enter a positive number"); return; }
    // Final confirm before persisting — the user will receive a notification.
    const verb = adjustDirection === "deduct" ? "deduct" : "add";
    const ok = window.confirm(
      `Are you sure you want to ${verb} ${num} credit${num !== 1 ? 's' : ''} ${verb === 'deduct' ? 'from' : 'to'} ${u.email}?\n\n` +
      `${adjustReason ? `Reason: ${adjustReason}\n\n` : ''}` +
      `The user will receive a notification about this change.`
    );
    if (!ok) return;
    setAdjustBusy(true);
    try {
      const signed = adjustDirection === "deduct" ? -num : num;
      await axios.post(`${API}/admin/users/${userId}/credits`, {
        amount: signed,
        reason: adjustReason || (adjustDirection === "deduct" ? "manual debit" : "manual credit"),
      }, { withCredentials: true });
      toast.success(`${adjustDirection === "deduct" ? "Deducted" : "Added"} ${num} credits`);
      setAdjustOpen(false); setAdjustAmount(""); setAdjustReason("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to adjust credits");
    } finally {
      setAdjustBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container flex justify-center py-20" data-testid="ledger-loading">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="page-container py-20 text-center text-sm text-gray-500" data-testid="ledger-error">
        Couldn't load credits ledger.
      </div>
    );
  }

  const u = data.user || {};
  const balance = u.credits_balance ?? 0;
  const granted = u.credits_granted_total ?? 0;
  const spent = u.credits_spent_total ?? 0;

  return (
    <div className="page-container" data-testid="admin-credits-ledger-page">
      {/* Breadcrumb / back */}
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-5">
        <Link to="/admin/users" className="hover:text-gray-700 inline-flex items-center gap-1" data-testid="back-to-users">
          <ChevronLeft className="w-4 h-4" />Users
        </Link>
        <span>/</span>
        <span className="text-gray-900 truncate">Credits ledger</span>
      </div>

      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] flex items-center gap-2">
            <Coins className="w-6 h-6 text-amber-500" />
            Credits Ledger
          </h1>
          <div className="mt-1 flex items-center gap-3 text-sm text-gray-500">
            <span className="inline-flex items-center gap-1.5"><UserIcon className="w-3.5 h-3.5" />{u.name || "—"}</span>
            <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{u.email || "—"}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setAdjustDirection("add"); setAdjustOpen(true); }} data-testid="btn-add-credits">
            <ArrowUpCircle className="w-4 h-4 mr-1.5 text-emerald-600" />Add credits
          </Button>
          <Button variant="outline" onClick={() => { setAdjustDirection("deduct"); setAdjustOpen(true); }} data-testid="btn-deduct-credits">
            <ArrowDownCircle className="w-4 h-4 mr-1.5 text-red-500" />Deduct credits
          </Button>
          <Button variant="outline" onClick={handleExport} disabled={!filtered.length} data-testid="btn-export-ledger">
            <Download className="w-4 h-4 mr-1.5" />Export CSV
          </Button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <SummaryCard label="Current balance" value={balance} accent="text-emerald-700" testid="summary-balance" />
        <SummaryCard label="Lifetime granted" value={granted} testid="summary-granted" />
        <SummaryCard label="Lifetime spent" value={spent} testid="summary-spent" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search description or reason..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="ledger-search"
          />
        </div>
        <div className="inline-flex border border-gray-200 rounded-md p-0.5 bg-white text-xs">
          {[
            { v: "all", l: "All" },
            { v: "credit", l: "Credits in" },
            { v: "debit", l: "Credits out" },
            { v: "payment", l: "Payments" },
          ].map(opt => (
            <button
              key={opt.v}
              className={`px-3 py-1.5 rounded transition-colors ${filter === opt.v ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"}`}
              onClick={() => setFilter(opt.v)}
              data-testid={`filter-${opt.v}`}
            >
              {opt.l}
            </button>
          ))}
        </div>
      </div>

      {/* Ledger table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white" data-testid="ledger-table">
        {filtered.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-12">
            {search || filter !== "all" ? "No entries match your filter." : "No credit history yet."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-xs text-gray-500">
                  <th className="px-4 py-2.5 font-medium">Date</th>
                  <th className="px-4 py-2.5 font-medium">Description</th>
                  <th className="px-4 py-2.5 font-medium">Reason</th>
                  <th className="px-4 py-2.5 font-medium text-right">Δ Credits</th>
                  <th className="px-4 py-2.5 font-medium text-right">Balance</th>
                  <th className="px-4 py-2.5 font-medium text-right">Amount</th>
                  <th className="px-4 py-2.5 font-medium text-right w-12"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const k = `${r.date || i}-${r.kind || ""}-${r.delta ?? ""}-${i}`;
                  const isVoided = !!r.voided_by;
                  const isStripeOriginated = (r.reason === "Top-up" || r.reason === "Subscription"
                    || r.reason === "Monthly renewal" || r.reason === "Refund") && r.amount_usd != null;
                  const canVoid = !!r.id && !isVoided && !isStripeOriginated && !r.voids_id;
                  return (
                    <tr key={k} className={`border-b border-gray-100 last:border-b-0 hover:bg-gray-50 ${isVoided ? "opacity-50 line-through" : ""}`} data-testid={`ledger-row-${i}`}>
                      <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{fmt(r.date)}</td>
                      <td className="px-4 py-2.5 text-gray-800">
                        {r.description}
                        {r.kind === "payment" && r.status && r.status !== "paid" && (
                          <span className="ml-2 text-[10px] uppercase font-semibold text-amber-700">{r.status}</span>
                        )}
                        {isVoided && (
                          <span className="ml-2 text-[10px] uppercase font-semibold text-gray-500 no-underline inline-block">voided</span>
                        )}
                        {/* Audit metadata — only present on referral / admin
                            top-up rows. Surfaces the inviter / invitee context
                            so admins can investigate referral abuse without
                            cross-referencing user ids in the DB. */}
                        {r.meta && (r.meta.referee_id || r.meta.referrer_id) && (
                          <div className="mt-1 flex flex-wrap gap-1 text-[10px] text-gray-500 no-underline">
                            {r.meta.referee_email && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 rounded">
                                Invited: <span className="font-mono text-emerald-900">{r.meta.referee_email}</span>
                                {r.meta.referee_name ? ` (${r.meta.referee_name})` : ""}
                              </span>
                            )}
                            {r.meta.referrer_email && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 border border-blue-200 rounded">
                                From: <span className="font-mono text-blue-900">{r.meta.referrer_email}</span>
                              </span>
                            )}
                            {(r.meta.referee_id || r.meta.referrer_id) && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-50 border border-gray-200 rounded text-gray-500 font-mono">
                                id: {(r.meta.referee_id || r.meta.referrer_id).slice(0, 12)}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-500 max-w-[260px] truncate" title={r.reason}>{r.reason || "—"}</td>
                      <td className={`px-4 py-2.5 text-right font-mono font-semibold ${r.delta > 0 ? "text-emerald-700" : r.delta < 0 ? "text-red-600" : "text-gray-400"}`}>
                        {r.delta > 0 ? "+" : ""}{r.delta || "—"}
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-500 font-mono">{r.balance_after ?? "—"}</td>
                      <td className="px-4 py-2.5 text-right text-gray-500 font-mono">{r.amount_usd ? `$${r.amount_usd.toFixed(2)}` : "—"}</td>
                      <td className="px-2 py-2.5 text-right">
                        {canVoid ? (
                          <button
                            onClick={() => voidEntry(r)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors no-underline"
                            title="Void this entry (creates a compensating row, audit-preserving)"
                            data-testid={`void-${r.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Adjust dialog */}
      {adjustOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => !adjustBusy && setAdjustOpen(false)} data-testid="adjust-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
              {adjustDirection === "deduct" ? (
                <><ArrowDownCircle className="w-5 h-5 text-red-500" />Deduct credits</>
              ) : (
                <><ArrowUpCircle className="w-5 h-5 text-emerald-600" />Add credits</>
              )}
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              {adjustDirection === "deduct"
                ? `Deduct credits from ${u.email}'s balance.`
                : `Grant credits to ${u.email}'s balance.`}
              {" "}Logged to credits ledger with your email.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Amount</label>
                <Input
                  type="number"
                  min="1"
                  value={adjustAmount}
                  onChange={(e) => setAdjustAmount(e.target.value)}
                  placeholder="50"
                  data-testid="adjust-amount"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Reason (optional)</label>
                <Input
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  placeholder="e.g. Goodwill credit for failed deck"
                  data-testid="adjust-reason"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setAdjustOpen(false)} disabled={adjustBusy}>Cancel</Button>
              <Button
                onClick={submitAdjust}
                disabled={adjustBusy}
                className={adjustDirection === "deduct" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                data-testid="adjust-submit"
              >
                {adjustBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : (adjustDirection === "deduct" ? "Confirm deduction" : "Add credits")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, accent = "text-gray-900", testid }) {
  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid={testid}>
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className={`text-2xl font-bold font-['Manrope'] ${accent}`}>{value}</div>
    </div>
  );
}
